import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Scroll-triggered reveal for the blog section header (label, title, subtitle, ornament).
 */
export class BlogSectionReveal {
  private section: HTMLElement | null = null;
  private header: HTMLElement | null = null;
  private label: HTMLElement | null = null;
  private title: HTMLElement | null = null;
  private subtitle: HTMLElement | null = null;
  private ornament: HTMLElement | null = null;
  private timeline: gsap.core.Timeline | null = null;

  constructor() {
    this.section = document.querySelector('.blog-section');
    if (!this.section) return;

    this.header = this.section.querySelector('.blog-section-header');
    this.label = this.section?.querySelector('.blog-section-label');
    this.title = this.section?.querySelector('.blog-section-title');
    this.subtitle = this.section?.querySelector('.blog-section-subtitle');
    this.ornament = this.section?.querySelector('.blog-section-ornament');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      gsap.set([this.label, this.title, this.subtitle, this.ornament].filter(Boolean), {
        opacity: 1,
        y: 0,
      });
      return;
    }

    this.setInitialStates();
    this.setupScrollReveal();
  }

  private setInitialStates(): void {
    const elements = [this.label, this.title, this.subtitle, this.ornament].filter(Boolean);
    elements.forEach((el) => gsap.set(el, { opacity: 0, y: 32, force3D: true }));
  }

  private setupScrollReveal(): void {
    if (!this.header && !this.section) return;
    const trigger = this.header ?? this.section;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger,
        start: 'top 90%',
        once: true,
        invalidateOnRefresh: true,
      },
      defaults: { ease: 'power3.out', force3D: true },
    });

    if (this.label) tl.to(this.label, { opacity: 1, y: 0, duration: 0.5 }, 0);
    if (this.title) {
      tl.to(
        this.title,
        { opacity: 1, y: 0, duration: 0.7, ease: 'back.out(1.15)' },
        0.08
      );
    }
    if (this.subtitle) tl.to(this.subtitle, { opacity: 1, y: 0, duration: 0.5 }, 0.2);
    if (this.ornament) tl.to(this.ornament, { opacity: 1, y: 0, duration: 0.45 }, 0.15);

    tl.set([this.label, this.title, this.subtitle, this.ornament].filter(Boolean), {
      clearProps: 'transform',
    });

    this.timeline = tl;
  }

  dispose(): void {
    this.timeline?.scrollTrigger?.kill();
    this.timeline?.kill();
    this.timeline = null;
    this.section = null;
    this.header = null;
    this.label = null;
    this.title = null;
    this.subtitle = null;
    this.ornament = null;
  }
}
